import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Button from 'components/ui/Button';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { label: 'Home', path: '/home-landing', icon: 'Home' },
    { label: 'Accommodations', path: '/accommodation-details', icon: 'Bed' },
    { label: 'Resorts', path: '/resorts-overview', icon: 'Mountain' },
    { label: 'Activities', path: '/activities-experiences', icon: 'Activity' },
    { label: 'Gallery', path: '/photo-gallery', icon: 'Camera' },
    { label: 'Contact', path: '/contact-directions', icon: 'MapPin' }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActiveRoute = (path) => {
    return location?.pathname === path;
  };

  const handleCallNow = () => {
    window.open('tel:+918277385225', '_self');
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-organic ${
      isScrolled ? 'bg-background/95 backdrop-blur-sm shadow-organic' : 'bg-background'
    }`}>
      <div className="w-full">
        <div className="flex items-center justify-between h-16 px-6 lg:px-8">
          {/* Logo */}
          <Link 
            to="/home-landing" 
            className="flex items-center space-x-2 transition-micro hover:opacity-80"
            onClick={closeMobileMenu}
          >
            <div className="flex items-center justify-center w-10 h-10">
              <Image
                src="/assets/images/Logo.png"
                alt="Dandeli Adventure Resorts"
                className="w-10 h-10 object-contain"
              />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-heading font-semibold text-primary">
                Dandeli Adventure
              </h1>
              <p className="text-xs text-text-secondary font-caption -mt-1">
                            Resorts
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {navigationItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-organic ${
                  isActiveRoute(item?.path)
                    ? 'text-primary bg-primary/10' :'text-text-primary hover:text-primary hover:bg-primary/5'
                }`}
              >
                <Icon name={item?.icon} size={16} />
                <span>{item?.label}</span>
              </Link>
            ))}
          </nav>

          {/* Call Now Button & Book Now Button & Mobile Menu Toggle */}
          <div className="flex items-center space-x-4">
            {/* Animated Call Now Button */}
            <Button
              variant="outline"
              size="sm"
              iconName="Phone"
              iconPosition="left"
              className="hidden sm:flex call-bounce border-accent text-accent hover:bg-accent hover:text-accent-foreground"
              onClick={handleCallNow}
            >
              Call Now
            </Button>

            <Button
              variant="default"
              size="sm"
              iconName="Calendar"
              iconPosition="left"
              className="hidden sm:flex"
              onClick={() => window.location.href = '/booking-engine'}
            >
              Book Now
            </Button>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden p-2 rounded-md text-text-primary hover:text-primary hover:bg-primary/5 transition-micro"
              aria-label="Toggle mobile menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-background border-t border-border shadow-organic-lg">
            <nav className="px-6 py-4 space-y-2">
              {navigationItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-base font-medium transition-organic ${
                    isActiveRoute(item?.path)
                      ? 'text-primary bg-primary/10' :'text-text-primary hover:text-primary hover:bg-primary/5'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                </Link>
              ))}
              
              {/* Mobile Call Now & Book Now Buttons */}
              <div className="pt-4 border-t border-border space-y-3">
                <Button
                  variant="outline"
                  size="default"
                  iconName="Phone"
                  iconPosition="left"
                  fullWidth
                  onClick={() => {
                    closeMobileMenu();
                    handleCallNow();
                  }}
                  className="border-accent text-accent hover:bg-accent hover:text-accent-foreground"
                >
                  Call Now - +91 8277385225
                </Button>
                <Button
                  variant="default"
                  size="default"
                  iconName="Calendar"
                  iconPosition="left"
                  fullWidth
                  onClick={() => {
                    closeMobileMenu();
                    window.location.href = '/booking-engine';
                  }}
                >
                  Book Now
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;